<template>
  <ExternalQueryList
      headline="Articles"
      endpoint="/api/v2/content-articles"
      :endpoint-filters="{portal_id: portal.id}"
      :headlines="headlines"
      :filters="filters"
      :can-destroy="() => true"
      :can-update="() => true"
      :get-row-style="contentArticle => getRowStyle(contentArticle)"
      @show="contentArticle => $router.push(getUrl(contentArticle))"
  >
    <template v-slot:default="{ row: contentArticle }">
      <td class="table__ellipsis">
        <router-link :to="getUrl(contentArticle)">
          {{ contentArticle.attributes.title }}
        </router-link>
      </td>

      <td>
        {{ contentArticle.attributes.content_article_category_name }}
      </td>

      <td>
        {{ contentArticle.attributes.editorial_type }}
      </td>

      <td>
        <div style="display: flex; justify-content: space-between">
          <div>
            {{ contentArticle.attributes.portal_company_name }}
          </div>

          <div style="white-space:nowrap;">
            <i
                v-if="contentArticle.attributes.is_copy"
                class="fa fa-share"
                v-tooltip="'Shared from another portal'"
            />

            <i v-else class="fa fa-share" style="opacity: 0"/>

            <i
                v-if="contentArticle.attributes.company_has_active_partnership && !contentArticle.attributes.portal_company_is_editorial"
                class="fa fa-book-reader"
                v-tooltip="'Active subscription'"
            />

            <i v-else class="fa fa-book-reader" style="opacity: 0"/>
          </div>
        </div>
      </td>

      <td>
        {{ contentArticle.attributes.publish_at | datetime }}
      </td>

      <td>
        <Avatar v-if="contentArticle.attributes.author_initials" :color="contentArticle.attributes.author_color">
          {{ contentArticle.attributes.author_initials }}
        </Avatar>
      </td>

      <td>
        <span v-if="!contentArticle.attributes.publish_at || !isPast(contentArticle.attributes.publish_at)"
              class="label label-info">
          Draft
        </span>

        <span v-else-if="isPast(contentArticle.attributes.unpublish_at)" class="label label-warning">
          Unpublished
        </span>

        <span v-else class="label label-success">
          Published
        </span>
      </td>
    </template>

    <template v-slot:headline-right>
      <router-link
          class="btn btn-white btn-sm"
          style="margin-top: 4px;"
          :to="'/portals/' + portal.id + '/content/articles/create'"
      >
        <i class="fa fa-plus"></i> Create
      </router-link>
    </template>

    <template v-slot:list-actions="{selected, update, resetSelected, canUpdateSelected}">
      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#add-article-tags-modal" data-toggle="modal">
          <i class="fa fa-plus"></i> Add Tags
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#add-article-reference-modal" data-toggle="modal">
          <i class="fa fa-plus"></i> Add Mentioned Companies
        </a>
      </li>

      <li :class="{'disabled': !canUpdateSelected}">
        <a href="#assign-article-category-modal" data-toggle="modal">
          <i class="fa fa-boxes"></i> Assign Category
        </a>
      </li>
    </template>

    <template v-slot:modals="{selected, update, resetSelected}">
      <AddTagsModal
          :selected="selected.map(contentArticle => contentArticle.id)"
          :portal="portal"
          @updated="updated($event, update, resetSelected)"
      />

      <AddArticleReferenceModal
          :selected="selected"
          :portal="portal"
          @updated="updated($event, update, resetSelected)"
      />

      <AssignCategoryModal
          :selected="selected"
          :content-article-categories="contentArticleCategories"
          @updated="updated($event, update, resetSelected)"
      />
    </template>
  </ExternalQueryList>
</template>

<script>
import moment from 'moment'
import Avatar from '../../../Avatar'
import ExternalQueryList from '../../../Panels/Lists/ExternalQuery'
import AddArticleReferenceModal from "../Modals/AddArticleReference.vue";
import AddTagsModal from "../Modals/AddTags";
import AssignCategoryModal from "../Modals/AssignCategory";

export default {
  components: {
    AddTagsModal,
    AddArticleReferenceModal,
    AssignCategoryModal,
    Avatar,
    ExternalQueryList
  },

  props: {
    portal: {
      required: true
    },

    users: {
      required: true
    },

    contentArticleCategories: {
      required: true
    }
  },

  computed: {
    headlines() {
      return [
        {width: 6, text: 'Title'},
        {width: 3, text: 'Category'},
        {width: 2, text: 'Type'},
        {width: 4, text: 'Company'},
        {width: 3, text: 'Publish At'},
        {width: 2, text: 'User'},
        {width: 2, text: 'Status'},
      ]
    },

    filters() {
      return [
        [
          {
            width: 5,
            widget: 'text',
            name: 'search',
            placeholder: 'Search...'
          },
          {
            width: 5,
            widget: 'select',
            name: 'portal_company_is_editorial',
            placeholder: 'Search partner type...',
            nullable: true,
            options: [{value: '1', label: 'Editorial partner'}, {value: '0', label: 'Partner'}]
          },
          {
            width: 5,
            widget: 'date',
            name: 'publish_from',
            placeholder: 'Publish from...'
          },
          {
            width: 5,
            widget: 'date',
            name: 'publish_to',
            placeholder: 'Publish to...'
          },
          {
            width: 4,
            widget: 'button',
            class: 'btn-white',
            text: '<i class="fa fa-times"></i> Clear',
            event: 'clear'
          }
        ],
        [
          {
            width: 5,
            widget: 'select',
            name: 'editorial_type',
            placeholder: 'Search types...',
            nullable: true,
            options: [{value: 'Spor 1', label: 'Spor 1'}, {value: 'Spor 2', label: 'Spor 2'}]
          },
          {
            width: 5,
            widget: 'select',
            name: 'content_article_category_id',
            placeholder: 'Search article categories...',
            nullable: true,
            options: this.contentArticleCategories.map(contentArticleCategory => ({
              value: contentArticleCategory.id,
              label: contentArticleCategory.attributes.name
            }))
          },
          {
            width: 5,
            widget: 'select',
            name: 'user_id',
            placeholder: 'Search users...',
            nullable: true,
            options: this.users.map(user => ({
              value: user.id,
              label: user.attributes.full_name
            }))
          },
          {
            width: 5,
            widget: 'typeahead',
            name: 'company_id',
            placeholder: 'Search companies...',
            endpoint: '/api/v2/companies'
          },
          {
            width: 4,
            widget: 'submit'
          }
        ]
      ]
    },
  },

  methods: {
    getUrl(contentArticle) {
      return '/portals/' + this.portal.id + '/content/articles/' + contentArticle.id + '/edit'
    },

    isPast(value) {
      return moment().isAfter(value)
    },

    updated(company, update, resetSelected) {
      update(company)

      resetSelected()
    },

    getRowStyle(contentArticle) {
      const schedule = contentArticle.attributes.content_article_flag_schedules.filter(schedule => schedule.is_active)[0]

      return schedule ? {'background-color': schedule.content_article_flag_color} : {}
    }
  }
}
</script>
